import React, { useEffect } from 'react';
import { Search, Filter, Brain, Target, Edit, BookOpen } from 'lucide-react';
import MathTopicCard from './MathTopicCard';

const MathematicsTopics = ({
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  categories,
  filteredTopics,
  autofillFromTemplate,
  generateFlashCardsFromTopic,
  startQuizFromTopic
}) => {
  // Initialize MathJax when component mounts
  useEffect(() => {
    if (window.MathJax) {
      window.MathJax.typesetPromise();
    }
  }, [filteredTopics]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">數學課題</h2>
        <p className="text-gray-600 text-lg">
          探索HKDSE數學課程的各個主題，包含詳細的模組和範例
        </p>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="搜尋數學課題..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white min-w-[150px]"
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-sm text-gray-500">
          找到 {filteredTopics.length} 個課題
        </div>
      </div>

      {/* Topics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTopics.map((topic) => (
          <MathTopicCard
            key={topic.id}
            topic={topic}
            onUseTemplate={autofillFromTemplate}
            onGenerateFlashCards={generateFlashCardsFromTopic}
            onStartQuiz={startQuizFromTopic}
          />
        ))}
      </div>

      {/* No Results */}
      {filteredTopics.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <BookOpen className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">沒有找到相關課題</h3>
            <p>請嘗試調整搜尋條件或選擇不同的分類</p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mt-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm">
            <Edit className="w-6 h-6 text-blue-500" />
            <div>
              <h4 className="font-medium">模板精靈</h4>
              <p className="text-sm text-gray-600">創建數學解題步驟</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm">
            <Brain className="w-6 h-6 text-green-500" />
            <div>
              <h4 className="font-medium">閃卡學習</h4>
              <p className="text-sm text-gray-600">記憶數學概念</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm">
            <Target className="w-6 h-6 text-red-500" />
            <div>
              <h4 className="font-medium">測驗練習</h4>
              <p className="text-sm text-gray-600">測試學習成果</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MathematicsTopics; 